import DesignTokens from '@tableau/design-tokens';
import { NavigationArrowLeftIcon } from '@tableau/icons/NavigationArrowLeftIcon';
import { ButtonType, ButtonWidget, InteractionState, Tooltip, TooltipPosition } from '@tableau/widgets';
import { CSSProperties } from 'react';
import React from 'react';

import { Telemetry } from '~commonInfra/services/Telemetry';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '~commonInfra/telemetryEvents/TelemetryEvents';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { Location } from '~commonInfra/utils/Location';
import { applyColor } from '~src/style/ColorService';

import { PreviousUrlInitialState } from '../../../bootstrap/redux/reducers/PreviousUrlReducer';
import { Messages } from '../../../codegen/Localize';
import { SpacesSiteUrls } from '../spaceComponents/SpacesSiteUrls';
import { PageHeaderButtonType } from './PageHeader';
import { PageHeaderStyles } from './PageHeaderStyles';
import CssVariables = PageHeaderStyles.CssVariables;
import cssVar = PageHeaderStyles.CssVariables.cssVar;

export const iconHeight: number = 24;
export interface BackButtonProps {
  readonly previousUrl: string;
  readonly parentId: string;
  readonly siteUrls: SpacesSiteUrls;
  readonly serverUrls: ServerUrls;
  readonly isOnDatasourceAnalyticsViz: boolean;
  readonly isOnSiteStatusViz: boolean;
  readonly isOnServerStatusViz: boolean;
  readonly buttonType: PageHeaderButtonType;
  readonly styleOverrides?: React.CSSProperties;
  readonly wrapperDivStyleOverrides?: React.CSSProperties;
}

const backButtonHoverStyle: CSSProperties = {
  backgroundColor: cssVar(CssVariables.headerBackgroundHoverColor),
  borderRadius: 2
};

export class BackButton extends React.PureComponent<BackButtonProps> {
  public render() {
    return (
      <div style={{...PageHeaderStyles.BackButtonWrapperDiv, ...this.props.wrapperDivStyleOverrides}}>
        <Tooltip getContent={this.getTooltipContent} position={TooltipPosition.BottomRightCorner} testId={'back-button-tooltip'} >
          <ButtonWidget
            buttonType={ButtonType.IconOutline}
            testId="back"
            getContent={this.getButtonContent}
            handleClick={this.handleClick}
            style={{...PageHeaderStyles.BackButton, ...this.props.styleOverrides}}
          />
        </Tooltip>
      </div>);
  }

  private getButtonContent = (state: InteractionState): JSX.Element => {
    // alt supplies the icon-only button's accessible name; mirrors the pre-migration <img alt={Messages.back()}>.
    const useActiveIcon: boolean = state.hover || state.active || state.focus;
    const iconColor = useActiveIcon
      ? applyColor(DesignTokens.color.font.default.default, '#333')
      : applyColor(DesignTokens.color.font.default.secondary, '#666');
    const divStyle = {
      lineHeight: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...state.hover && backButtonHoverStyle
    };

    return (
      <div style={divStyle}>
        <NavigationArrowLeftIcon size={18} color={iconColor} alt={Messages.back()}/>
      </div>);
  };

  private handleClick = (): void => {
    let url: string = '';

    if (this.props.isOnSiteStatusViz) {
      url = this.props.siteUrls.statusUrl();

    } else if (this.props.isOnServerStatusViz) {
      url = this.props.serverUrls.statusUrl();

    } else if (this.props.previousUrl === PreviousUrlInitialState) {
      if (this.props.isOnDatasourceAnalyticsViz) {
        url = this.props.siteUrls.datasourceUrl(this.props.parentId);
      } else {
        url = this.props.siteUrls.workbookUrl(this.props.parentId);
      }

    } else {
      url = this.props.previousUrl;
    }

    Telemetry.sendEvent<PageHeaderButtonClickEvent>({
      type: TelemetryEvents.PageHeaderButtonClick,
      button: 'back-button',
      action: 'open'
    });

    Location.instance.setUrl(url);
  };

  private getTooltipContent = (): JSX.Element => {
    return (
    <span>
      {Messages.back()}
    </span>);
  };
}
